import { RacemapColors } from '@racemap/utilities/consts/common';
import type { ExpressionSpecification } from 'mapbox-gl';

const colorExpression = (
  defaultColor: string = RacemapColors.PaleBlue,
  colorKey = 'user_color',
): ExpressionSpecification => ['coalesce', ['get', colorKey], defaultColor];
const lineWidthExpression = (
  defaultLineWidth = 2,
  lineWidthKey = 'user_lineWidth',
): ExpressionSpecification => ['coalesce', ['get', lineWidthKey], defaultLineWidth];
const opacityExpression = (): ExpressionSpecification => [
  'case',
  ['boolean', ['get', 'user_hidden'], false],
  0.5,
  1,
];

export default [
  {
    id: 'gl-draw-polygon-fill-inactive',
    type: 'fill',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': colorExpression(),
      'fill-outline-color': colorExpression(),
      'fill-opacity': 0.1,
    },
  },
  {
    id: 'gl-draw-polygon-fill-active',
    type: 'fill',
    filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': '#fbb03b',
      'fill-outline-color': '#fbb03b',
      'fill-opacity': 0.1,
    },
  },
  {
    id: 'gl-draw-polygon-and-line-midpoint',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
    paint: {
      'circle-radius': 3,
      'circle-color': '#51230F',
    },
  },
  {
    id: 'gl-draw-polygon-stroke-inactive',
    type: 'line',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': colorExpression(),
      'line-width': lineWidthExpression(),
      'line-opacity': opacityExpression(),
    },
  },
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line',
    filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': colorExpression(),
      'line-dasharray': [0.2, 2],
      'line-width': lineWidthExpression(),
    },
  },
  {
    id: 'gl-draw-line-inactive',
    type: 'line',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'LineString']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': colorExpression(),
      'line-width': lineWidthExpression(),
      'line-opacity': opacityExpression(),
    },
  },
  {
    id: 'gl-draw-line-active',
    type: 'line',
    filter: ['all', ['==', '$type', 'LineString'], ['==', 'active', 'true']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': colorExpression('#fbb03b'),
      'line-dasharray': [0.2, 2],
      'line-width': lineWidthExpression(),
    },
  },
  {
    id: 'gl-draw-polygon-and-line-vertex-stroke-inactive',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 5,
      'circle-color': '#fff',
    },
  },
  {
    id: 'gl-draw-polygon-and-line-vertex-inactive',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 3,
      'circle-color': '#fbb03b',
    },
  },
  {
    id: 'gl-draw-point-point-stroke-inactive',
    type: 'circle',
    filter: [
      'all',
      ['==', 'active', 'false'],
      ['==', '$type', 'Point'],
      ['==', 'meta', 'feature'],
      ['!has', 'user_lineStringId'],
    ],
    paint: {
      'circle-radius': 5,
      'circle-opacity': opacityExpression(),
      'circle-color': '#fff',
    },
  },
  {
    id: 'gl-draw-point-inactive',
    type: 'circle',
    filter: [
      'all',
      ['==', 'active', 'false'],
      ['==', '$type', 'Point'],
      ['==', 'meta', 'feature'],
      ['!has', 'user_lineStringId'],
    ],
    paint: {
      'circle-radius': 3,
      'circle-color': colorExpression(),
      'circle-opacity': opacityExpression(),
    },
  },
  {
    id: 'gl-draw-point-stroke-active',
    type: 'circle',
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['==', 'active', 'true'],
      ['!=', 'meta', 'midpoint'],
      ['!has', 'user_lineStringId'],
    ],
    paint: {
      'circle-radius': 7,
      'circle-color': '#fff',
    },
  },
  {
    id: 'gl-draw-point-active',
    type: 'circle',
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['!=', 'meta', 'midpoint'],
      ['==', 'active', 'true'],
      ['!has', 'user_lineStringId'],
    ],
    paint: {
      'circle-radius': 5,
      'circle-color': '#E85B1E',
    },
  },
  {
    id: 'near-by-mouse-point',
    type: 'circle',
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['==', 'meta', 'feature'],
      ['==', 'user_isNearPoint', true],
    ],
    paint: {
      'circle-radius': 4,
      'circle-color': RacemapColors.Orange,
    },
  },
];
